import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { BellOff, Check, Loader2, MailX } from "lucide-react";
import { postApi, useApi } from "@/lib/api";
import { BackLink, NotFound } from "@/components/site/DetailBits";
import { TypeChips } from "@/components/site/TypeChips";

const btn = "inline-flex items-center gap-3 font-display uppercase text-lg px-8 py-4 transition-colors duration-200 disabled:opacity-60";

const AlertTypes = ({ token, initial }) => {
  const [types, setTypes] = useState(initial || []);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await postApi("/newsletter/alert-types", { token, types });
      toast.success(types.length ? "C'est noté : tu ne recevras que ces catégories." : "C'est noté : tu recevras tous les événements.");
    } catch {
      toast.error("Ce lien n'est plus valide.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-10 border-t border-white/10 pt-8" data-testid="alert-types">
      <p className="text-white font-bold uppercase tracking-[0.15em] text-sm">Catégories suivies <span className="text-zinc-400 normal-case tracking-normal font-normal">(rien de coché = tous les événements)</span></p>
      <div className="mt-4"><TypeChips value={types} onChange={setTypes} testId="alert-type" /></div>
      <button onClick={save} disabled={busy} data-testid="alert-types-save-btn" className={`${btn} mt-6 bg-white text-black hover:bg-primary hover:text-white`}>
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} Enregistrer mes catégories
      </button>
    </div>
  );
};

export default function Unsubscribe() {
  const { token } = useParams();
  const { data, isLoading, isError } = useApi(`/newsletter/subscription/${token}`);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (scope) => {
    setBusy(true);
    try {
      await postApi("/newsletter/unsubscribe", { token, scope });
      setDone(scope);
    } catch {
      toast.error("Ce lien n'est plus valide.");
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <section className="pt-48 pb-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></section>;
  if ((isError || !data) && !done) return <NotFound to="/" label="Retour à l'accueil" />;

  return (
    <section className="pt-40 pb-32 max-w-[900px] mx-auto px-5 md:px-10" data-testid="unsubscribe-page">
      <BackLink to="/" label="Retour à l'accueil" />
      <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white mt-8">
        Mes alertes<span className="text-primary">.</span>
      </h1>
      {done ? (
        <p className="text-white text-lg mt-8" data-testid="unsubscribe-done">
          {done === "all" ? "C'est fait : tu ne recevras plus aucun e-mail de LGTDZ." : "C'est fait : tu ne recevras plus d'alertes événements. Tu restes inscrit·e à la newsletter."}
        </p>
      ) : (
        <>
          <p className="text-zinc-300 text-lg mt-8" data-testid="unsubscribe-summary">
            Adresse : <strong className="text-white">{data.email}</strong>
            <br />
            Villes suivies : <strong className="text-white">{data.cities?.length ? data.cities.join(", ") : "aucune"}</strong>
          </p>
          {data.cities?.length > 0 && <AlertTypes token={token} initial={data.types} />}
          <div className="flex flex-wrap gap-3 mt-10">
            {data.cities?.length > 0 && (
              <button onClick={() => run("alerts")} disabled={busy} data-testid="unsubscribe-alerts-btn" className={`${btn} bg-primary text-white hover:bg-white hover:text-black`}>
                <BellOff className="w-5 h-5" /> Arrêter les alertes
              </button>
            )}
            <button onClick={() => run("all")} disabled={busy} data-testid="unsubscribe-all-btn" className={`${btn} border-2 border-white text-white hover:bg-white hover:text-black`}>
              <MailX className="w-5 h-5" /> Me désinscrire de tout
            </button>
          </div>
        </>
      )}
    </section>
  );
}
